import { ApolloLink, NextLink, Operation } from '@apollo/client';
import { Observable } from '@apollo/client';
import { GraphQLError } from 'graphql';

import { LaunchDarklyFlag } from 'state/launchdarkly/flags';
import { getAllFlags } from 'state/launchdarkly/platform-client';
import { GraphQLErrorCodes } from 'utils/errors/types';
import { PspRefusedCodes } from 'utils/errors/types';

import {
  COMMIT_ORDER_TEST_ERROR_MODAL_EXTENSION_KEY,
  COMMIT_ORDER_TEST_ERROR_MODAL_GQL_VALUES,
} from './commit-order-test-error-modal-constants';

const COMMIT_ORDER_OPERATION_NAME = 'CommitOrder';

function getCommitOrderTestErrorFlagValue(
  flags: Record<string, unknown> | null | undefined
): string | undefined {
  if (!flags || typeof flags !== 'object') {
    return undefined;
  }
  const value = flags[LaunchDarklyFlag.COMMIT_ORDER_TEST_ERROR_MODAL];
  return typeof value === 'string' ? value : undefined;
}

export { COMMIT_ORDER_TEST_ERROR_MODAL_EXTENSION_KEY };

/**
 * When the LaunchDarkly flag for commit-order test errors is set to a non-empty string
 * for the current user, intercepts the CommitOrder mutation and returns a GraphQL error.
 * - Known PSP/GQL codes: error is built with correct extensions so the app shows the real modal.
 * - Other values: used as the error message (generic modal).
 */
export const commitOrderTestErrorModalLink = new ApolloLink(
  (operation: Operation, forward: NextLink) => {
    if (operation.operationName !== COMMIT_ORDER_OPERATION_NAME) {
      return forward(operation);
    }

    let testErrorValue: string | undefined;
    try {
      const flags = getAllFlags?.();
      testErrorValue = getCommitOrderTestErrorFlagValue(
        flags as Record<string, unknown> | null | undefined
      );
    } catch {
      return forward(operation);
    }

    const raw =
      typeof testErrorValue === 'string' && testErrorValue.trim() !== ''
        ? testErrorValue.trim()
        : undefined;

    if (!raw) {
      return forward(operation);
    }

    const extensions = getTestErrorExtensions(raw);
    const error = new GraphQLError(
      `[Commit order test] ${raw}`,
      undefined,
      undefined,
      undefined,
      [operation.operationName],
      undefined,
      {
        ...extensions,
        [COMMIT_ORDER_TEST_ERROR_MODAL_EXTENSION_KEY]: true,
      }
    );

    return new Observable(observer => {
      observer.next({ errors: [error] });
      observer.complete();
    });
  }
);

function getTestErrorExtensions(flagValue: string): Record<string, unknown> | undefined {
  const pspCode = flagValue as keyof typeof PspRefusedCodes;
  if (pspCode in PspRefusedCodes) {
    return {
      code: GraphQLErrorCodes.PAYMENTS_DECLINED_ERROR,
      responseMessage: pspCode,
    };
  }
  if (COMMIT_ORDER_TEST_ERROR_MODAL_GQL_VALUES.includes(flagValue as GraphQLErrorCodes)) {
    return { code: flagValue };
  }
  return undefined;
}
